import { AnalyzerEntity, SampleAsset } from '@telespot/domain';
import { IMethodTypeAiEnabledPort } from '../../ports/method-type-ai-enabled.port';
import { AlgorithmAnalyzedAssetsCounter } from '../../services/algorithm-analyzed-assets.counter';

export interface SampleAiProgressState {
  analystId: string;
  analystEntity: AnalyzerEntity;
  updatedAt?: Date;
}

export type SampleAiAnalysisStatus = 'inProgress' | 'ready' | 'error';

export interface SampleAiProgressOutput {
  status?: SampleAiAnalysisStatus;
}

export interface ResolveSampleAiProgressParams {
  sampleId: string;
  methodTypeId: string;
  sampleAssets: SampleAsset[];
  analysisStates: SampleAiProgressState[];
  methodTypeAiEnabled: IMethodTypeAiEnabledPort;
  analyzedAssetsCounter: AlgorithmAnalyzedAssetsCounter;
}

export function resolveAlgorithmId(analysisStates: SampleAiProgressState[]): string | undefined {
  const algorithmState = [...analysisStates]
    .sort((left, right) => (right.updatedAt?.getTime() ?? 0) - (left.updatedAt?.getTime() ?? 0))
    .find((state) => state.analystEntity === 'algorithm');

  return algorithmState?.analystId;
}

export async function resolveSampleAiProgress(
  params: ResolveSampleAiProgressParams
): Promise<SampleAiProgressOutput> {
  const { sampleId, methodTypeId, sampleAssets, analysisStates, methodTypeAiEnabled, analyzedAssetsCounter } = params;

  const assetIds = sampleAssets.map((sa) => sa.asset.id).filter((id): id is string => !!id);
  const totalAssets = assetIds.length;

  if (totalAssets === 0) {
    return {};
  }

  const algorithmId = resolveAlgorithmId(analysisStates);
  if (!algorithmId) {
    const aiEnabled = await methodTypeAiEnabled.isEnabled(methodTypeId);
    if (!aiEnabled) {
      return {};
    }
    return { status: 'inProgress' };
  }

  const { analyzed, failed } = await analyzedAssetsCounter.count({
    sampleId,
    algorithmId,
    assetIds,
  });

  // A recorded failure is terminal (non-retryable): the sample can no longer
  // reach 'ready' on its own, so it is reported as an error right away even
  // if other assets are still being processed.
  if (failed > 0) {
    return { status: 'error' };
  }

  return { status: analyzed >= totalAssets ? 'ready' : 'inProgress' };
}
